import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  AuthRoutes,
  RouteParamsTypes,
  Modules,
  NonAuthRoutes,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../enums';
import ProtectedRoute from '../components/ProtectedRoute';
import StatusMasterList from '../modules/masters/status/list';
import StatusMasterForm from '../modules/masters/status/form';
import BillingCycleList from '../modules/masters/billing-cycles/list';
import BillingCycleForm from '../modules/masters/billing-cycles/form';
import CurrencyList from '../modules/masters/currencies/list';
import CurrencyForm from '../modules/masters/currencies/form';
import ProductList from '../modules/masters/products/list';
import ProductForm from '../modules/masters/products/form';
import CountryList from '../modules/masters/countries/list';
import CountryForm from '../modules/masters/countries/form';
import TimezoneList from '../modules/masters/timezones/list';
import TimezoneForm from '../modules/masters/timezones/form';
import CategoryList from '../modules/masters/categories/list';
import CategoryForm from '../modules/masters/categories/form';
import RoleList from '../modules/masters/roles/list';
import RoleForm from '../modules/masters/roles/form';
import ConfigKeyList from '../modules/masters/config-keys/list';
import ConfigKeyForm from '../modules/masters/config-keys/form';
import NotifTemplateList from '../modules/masters/notif-templates/list';
import NotifTemplateForm from '../modules/masters/notif-templates/form';
import ReceiptTemplateList from '../modules/masters/receipt-templates/list';
import ReceiptTemplateForm from '../modules/masters/receipt-templates/form';
import PlanList from '../modules/masters/plans/list';
import PlanForm from '../modules/masters/plans/form';
import { StatusMasterType } from '../services/master.service';

interface StatusTypeConfig {
  path: string;
  type: StatusMasterType;
  hasActiveState: boolean;
}

const STATUS_TYPES: StatusTypeConfig[] = [
  { path: AuthRoutes.tenantStatuses, type: 'tenantStatuses', hasActiveState: true },
  { path: AuthRoutes.subscriptionStatuses, type: 'subscriptionStatuses', hasActiveState: true },
  { path: AuthRoutes.paymentStatuses, type: 'paymentStatuses', hasActiveState: false },
];

const accessibleTo = [
  {
    [PermissionModulesEnum.Masters]: [
      PermissionActionsEnum.View,
      PermissionActionsEnum.Add,
      PermissionActionsEnum.Update,
      PermissionActionsEnum.Delete,
    ],
  },
];

const Masters: React.FC = () => {
  return (
    <Routes>
      {STATUS_TYPES.map(({ path, type, hasActiveState }) => (
        <Route key={path} path={path}>
          <Route
            path={path + RouteParamsTypes.blank}
            element={
              <ProtectedRoute
                component={() => <StatusMasterList type={type} routePath={path} hasActiveState={hasActiveState} />}
                accessibleTo={accessibleTo}
              />
            }
          />
          <Route
            path={path + RouteParamsTypes.add}
            element={
              <ProtectedRoute
                component={() => <StatusMasterForm type={type} routePath={path} hasActiveState={hasActiveState} />}
                accessibleTo={accessibleTo}
              />
            }
          />
          <Route
            path={path + RouteParamsTypes.edit + RouteParamsTypes.id}
            element={
              <ProtectedRoute
                component={() => <StatusMasterForm type={type} routePath={path} hasActiveState={hasActiveState} />}
                accessibleTo={accessibleTo}
              />
            }
          />
        </Route>
      ))}

      <Route path={AuthRoutes.billingCycles}>
        <Route
          path={AuthRoutes.billingCycles + RouteParamsTypes.blank}
          element={<ProtectedRoute component={BillingCycleList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.billingCycles + RouteParamsTypes.add}
          element={<ProtectedRoute component={BillingCycleForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.billingCycles + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={BillingCycleForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.currencies}>
        <Route
          path={AuthRoutes.currencies + RouteParamsTypes.blank}
          element={<ProtectedRoute component={CurrencyList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.currencies + RouteParamsTypes.add}
          element={<ProtectedRoute component={CurrencyForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.currencies + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={CurrencyForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.productMaster}>
        <Route
          path={AuthRoutes.productMaster + RouteParamsTypes.blank}
          element={<ProtectedRoute component={ProductList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.productMaster + RouteParamsTypes.add}
          element={<ProtectedRoute component={ProductForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.productMaster + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={ProductForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.countries}>
        <Route
          path={AuthRoutes.countries + RouteParamsTypes.blank}
          element={<ProtectedRoute component={CountryList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.countries + RouteParamsTypes.add}
          element={<ProtectedRoute component={CountryForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.countries + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={CountryForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.timezones}>
        <Route
          path={AuthRoutes.timezones + RouteParamsTypes.blank}
          element={<ProtectedRoute component={TimezoneList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.timezones + RouteParamsTypes.add}
          element={<ProtectedRoute component={TimezoneForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.timezones + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={TimezoneForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.categories}>
        <Route
          path={AuthRoutes.categories + RouteParamsTypes.blank}
          element={<ProtectedRoute component={CategoryList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.categories + RouteParamsTypes.add}
          element={<ProtectedRoute component={CategoryForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.categories + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={CategoryForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.roles}>
        <Route
          path={AuthRoutes.roles + RouteParamsTypes.blank}
          element={<ProtectedRoute component={RoleList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.roles + RouteParamsTypes.add}
          element={<ProtectedRoute component={RoleForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.roles + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={RoleForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.configKeys}>
        <Route
          path={AuthRoutes.configKeys + RouteParamsTypes.blank}
          element={<ProtectedRoute component={ConfigKeyList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.configKeys + RouteParamsTypes.add}
          element={<ProtectedRoute component={ConfigKeyForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.configKeys + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={ConfigKeyForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.notifTemplates}>
        <Route
          path={AuthRoutes.notifTemplates + RouteParamsTypes.blank}
          element={<ProtectedRoute component={NotifTemplateList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.notifTemplates + RouteParamsTypes.add}
          element={<ProtectedRoute component={NotifTemplateForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.notifTemplates + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={NotifTemplateForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.receiptTemplates}>
        <Route
          path={AuthRoutes.receiptTemplates + RouteParamsTypes.blank}
          element={<ProtectedRoute component={ReceiptTemplateList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.receiptTemplates + RouteParamsTypes.add}
          element={<ProtectedRoute component={ReceiptTemplateForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.receiptTemplates + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={ReceiptTemplateForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route path={AuthRoutes.plans}>
        <Route
          path={AuthRoutes.plans + RouteParamsTypes.blank}
          element={<ProtectedRoute component={PlanList} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.plans + RouteParamsTypes.add}
          element={<ProtectedRoute component={PlanForm} accessibleTo={accessibleTo} />}
        />
        <Route
          path={AuthRoutes.plans + RouteParamsTypes.edit + RouteParamsTypes.id}
          element={<ProtectedRoute component={PlanForm} accessibleTo={accessibleTo} />}
        />
      </Route>

      <Route
        path={RouteParamsTypes.any}
        element={<Navigate replace to={{ pathname: Modules.error + NonAuthRoutes.notFound }} />}
      />
    </Routes>
  );
};

export default Masters;
